import { AlertCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import invariant from 'tiny-invariant';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { CareersPageSettingsSection } from '@/modules/companies/components/careersPageSettings/CareersPageSettingsSection';
import { CompanyForm } from '@/modules/companies/components/CompanyForm';
import { CompanyNotesList } from '@/modules/companies/components/companyNotes/CompanyNotesList';
import { useUpdateCompanyMutation } from '@/modules/companies/mutations/useUpdateCompanyMutation';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import type { CompanyRequestApi } from '@/modules/companies/types';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/modules/ui/components/alert/Alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const CompanyEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const companyQuery = useCompanyQuery(id!);
  const updateCompanyMutation = useUpdateCompanyMutation();

  const handleSubmit = async (values: CompanyRequestApi) => {
    invariant(id, 'Company ID is required to submit CompanyEdit form');

    updateCompanyMutation.mutate(
      {
        id,
        ...values,
      },
      {
        onSuccess: () => {
          toast.success('Company has been updated');
          navigate('/companies');
        },
        onError: (error) => {
          toast.error(
            `Company has not been updated: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <Container>
      <PageHeader title="Edit Company" />

      {companyQuery.isLoading && <Loader />}
      {companyQuery.isError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Company not found</AlertDescription>
        </Alert>
      )}

      {companyQuery.data && (
        <div className="space-y-8">
          <Card>
            <CardHeader>
              <CardTitle>Company details</CardTitle>
              <CardDescription>
                Basic information about the company
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CompanyForm
                initialValues={companyQuery.data}
                submitText="Edit Company"
                isPending={updateCompanyMutation.isPending}
                onSubmit={handleSubmit}
                onCancel={() => navigate('/companies')}
              />
            </CardContent>
          </Card>

          {id && (
            <PermissionsGate permissions={[permissions.Inbound.Manage]}>
              <CareersPageSettingsSection companyId={id} />
            </PermissionsGate>
          )}

          {id && <CompanyNotesList companyId={id} />}
        </div>
      )}
    </Container>
  );
};

export default CompanyEdit;
